import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Mic, MicOff, AlertCircle } from "lucide-react";

const PLACEHOLDER = "Best monitor for work under $300 in Montreal";

interface SearchInputProps {
  onSubmit: (query: string) => void;
  isLoading: boolean;
  compact?: boolean;
}

type VoiceState = "idle" | "listening" | "processing" | "error" | "unsupported";

const IDLE_TIMEOUT_MS = 5000; // 5s silence before auto-stop
const MAX_RESTARTS = 3;

export default function SearchInput({ onSubmit, isLoading, compact }: SearchInputProps) {
  const [query, setQuery] = useState("");
  
  const [voiceState, setVoiceState] = useState<VoiceState>("idle");
  const [interimTranscript, setInterimTranscript] = useState("");
  const [voiceError, setVoiceError] = useState("");
  const recognitionRef = useRef<any>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const restartCountRef = useRef(0);
  const userStoppedRef = useRef(false);
  const wordCountRef = useRef(0);

  const hasSpeech =
    typeof window !== "undefined" &&
    ("SpeechRecognition" in window || "webkitSpeechRecognition" in window);

  useEffect(() => {
    if (!hasSpeech) setVoiceState("unsupported");
  }, [hasSpeech]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try { recognitionRef.current.abort(); } catch {}
      }
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, []);

  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      // Auto-stop after silence
      if (recognitionRef.current) {
        userStoppedRef.current = true;
        try { recognitionRef.current.stop(); } catch {}
      }
      setVoiceState("idle");
      setInterimTranscript("");
      recognitionRef.current = null;
    }, IDLE_TIMEOUT_MS);
  }, []);

  const stopListening = useCallback(() => {
    userStoppedRef.current = true;
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch {}
      recognitionRef.current = null;
    }
    setVoiceState("idle");
    setInterimTranscript("");
    restartCountRef.current = 0;
  }, []);

  const handleVoice = useCallback(async () => {
    if (voiceState === "listening") {
      stopListening();
      return;
    }

    setVoiceError("");
    userStoppedRef.current = false;
    restartCountRef.current = 0;
    wordCountRef.current = 0;

    // HTTPS / secure context check
    if (!window.isSecureContext) {
      setVoiceError("Voice input requires a secure connection (HTTPS).");
      setVoiceState("error");
      return;
    }

    const SpeechRecognition =
      (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setVoiceState("unsupported");
      return;
    }

    // Proactive mic permission check
    try {
      const permResult = await navigator.permissions.query({ name: "microphone" as PermissionName });
      if (permResult.state === "denied") {
        setVoiceError("Microphone access denied. Enable it in browser settings.");
        setVoiceState("error");
        return;
      }
    } catch {}

    function startRecognition() {
      const recognition = new SpeechRecognition();
      recognition.lang = "en-US";
      recognition.interimResults = true;
      recognition.continuous = true; // Stay open across pauses
      recognition.maxAlternatives = 1;

      recognition.onstart = () => {
        setVoiceState("listening");
        setInterimTranscript("");
        resetIdleTimer();
      };

      recognition.onresult = (event: any) => {
        resetIdleTimer(); // Reset idle timer on any speech activity
        let interim = "";
        let final = "";
        for (let i = 0; i < event.results.length; i++) {
          const result = event.results[i];
          if (result.isFinal) {
            final += result[0].transcript;
          } else {
            interim += result[0].transcript;
          }
        }

        if (final) {
          setQuery(prev => {
            const combined = prev ? `${prev} ${final}`.trim() : final;
            wordCountRef.current = combined.split(/\s+/).length;
            return combined;
          });
          setInterimTranscript("");
        } else {
          setInterimTranscript(interim);
        }
      };

      recognition.onerror = (event: any) => {
        console.error("[Voice] SpeechRecognition error:", event.error);
        const errorMap: Record<string, string> = {
          "not-allowed": "Microphone access denied. Enable it in browser settings.",
          "service-not-allowed": "Microphone access denied. Enable it in browser settings.",
          "no-speech": "", // Don't show error for no-speech, just restart
          "audio-capture": "No microphone found.",
          "network": "Speech service unavailable. Check your connection.",
          "aborted": "",
        };
        const msg = errorMap[event.error] || `Voice error: ${event.error}. Try again.`;
        
        if (event.error === "no-speech") {
          // Don't show error, just let onend handle restart
          return;
        }
        
        if (msg) setVoiceError(msg);
        if (event.error === "not-allowed" || event.error === "service-not-allowed") {
          setVoiceState("error");
          recognitionRef.current = null;
          if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
        }
      };

      recognition.onend = () => {
        // If user explicitly stopped, don't restart
        if (userStoppedRef.current) {
          setVoiceState("idle");
          setInterimTranscript("");
          recognitionRef.current = null;
          return;
        }

        // Auto-restart if within retry limit (handles browser-forced stops)
        if (restartCountRef.current < MAX_RESTARTS) {
          restartCountRef.current++;
          try {
            recognition.start();
            return;
          } catch {
            // Fall through to stop
          }
        }

        // Max restarts reached or start failed
        setVoiceState("idle");
        setInterimTranscript("");
        recognitionRef.current = null;
        if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      };

      recognitionRef.current = recognition;
      try {
        recognition.start();
      } catch (err) {
        console.error("[Voice] recognition.start() failed:", err);
        setVoiceError("Could not start voice input.");
        setVoiceState("idle");
      }
    }

    startRecognition();
  }, [voiceState, stopListening, resetIdleTimer]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const text = query.trim() || interimTranscript.trim();
    if (text && !isLoading) {
      stopListening();
      setQuery(text);
      onSubmit(text);
    }
  };

  const displayValue = voiceState === "listening" && interimTranscript
    ? (query ? `${query} ${interimTranscript}` : interimTranscript)
    : query;

  const currentWordCount = displayValue.trim().split(/\s+/).filter(Boolean).length;

  return (
    <form onSubmit={handleSubmit} className="w-full max-w-2xl mx-auto">
      <div className="relative group">

        {/* Listening indicator ring */}
        <AnimatePresence>
          {voiceState === "listening" && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="absolute -inset-0.5 rounded-2xl border-2 border-primary/40 pointer-events-none"
            >
              <motion.div
                className="absolute inset-0 rounded-2xl border-2 border-primary/20"
                animate={{ scale: [1, 1.02, 1] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              />
            </motion.div>
          )}
        </AnimatePresence>

        <div className="relative flex items-center bg-[hsl(220,13%,12%)] rounded-2xl border border-border/70 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.4)] focus-within:border-primary/60 focus-within:shadow-[0_0_0_2px_rgba(59,130,246,0.25),0_8px_30px_-6px_rgba(59,130,246,0.2)] transition-all duration-300">
          <input
            type="text"
            value={displayValue}
            onChange={(e) => { setQuery(e.target.value); setInterimTranscript(""); }}
            placeholder={voiceState === "listening" ? "Listening... speak naturally" : "Ask anything… (e.g. best monitor under $500)"}
            className={`flex-1 bg-transparent px-5 ${compact ? 'py-[16px]' : 'py-[22px] sm:py-[24px]'} text-foreground placeholder:text-muted-foreground/55 text-base sm:text-lg font-body outline-none min-w-0 ${
              voiceState === "listening" && interimTranscript ? "text-primary" : ""
            }`}
            disabled={isLoading}
          />
          <div className="flex items-center gap-1 mr-3 sm:mr-4 shrink-0">
            {hasSpeech && voiceState !== "unsupported" && (
              <motion.button
                type="button"
                onClick={handleVoice}
                disabled={isLoading}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`p-2.5 rounded-xl transition-colors relative ${
                  voiceState === "listening"
                    ? "bg-primary/20 text-primary"
                    : voiceState === "error"
                    ? "bg-destructive/15 text-destructive"
                    : "text-muted-foreground/35 hover:text-foreground/80 hover:bg-muted/30"
                }`}
                aria-label={voiceState === "listening" ? "Stop listening" : "Start voice input"}
              >
                {voiceState === "listening" ? (
                  <>
                    <MicOff className="w-4 h-4" />
                    <motion.span
                      className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-primary rounded-full"
                      animate={{ scale: [1, 1.4, 1], opacity: [1, 0.6, 1] }}
                      transition={{ duration: 1, repeat: Infinity }}
                    />
                  </>
                ) : (
                  <Mic className="w-4 h-4" />
                )}
              </motion.button>
            )}
            <motion.button
              type="submit"
              disabled={!(query.trim() || interimTranscript.trim()) || isLoading}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              aria-label="Search"
              className="p-3 rounded-lg bg-gradient-to-br from-primary to-primary/85 text-primary-foreground shadow-[0_2px_8px_rgba(59,130,246,0.25)] hover:shadow-[0_4px_12px_rgba(59,130,246,0.35)] hover:translate-y-[-1px] active:translate-y-[0.5px] active:shadow-[0_1px_4px_rgba(59,130,246,0.2)] disabled:opacity-20 disabled:cursor-not-allowed disabled:hover:translate-y-0 transition-all duration-200"
            >
              {isLoading ? (
                <motion.div
                  className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                />
              ) : (
                <ArrowRight className="w-5 h-5" />
              )}
            </motion.button>
          </div>
        </div>
      </div>

      {/* Voice status feedback */}
      <AnimatePresence>
        {voiceState === "listening" && (
          <motion.p
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="text-[11px] text-primary font-body mt-2 text-center flex items-center justify-center gap-1.5"
          >
            <motion.span
              className="inline-block w-1.5 h-1.5 bg-primary rounded-full"
              animate={{ scale: [1, 1.5, 1] }}
              transition={{ duration: 0.8, repeat: Infinity }}
            />
            Listening{currentWordCount > 0 ? ` (${currentWordCount} words)` : ""} — tap mic or submit when done
          </motion.p>
        )}
        {voiceError && voiceState !== "listening" && (
          <motion.p
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="text-[11px] text-destructive/80 font-body mt-2 text-center flex items-center justify-center gap-1.5"
          >
            <AlertCircle className="w-3 h-3" />
            {voiceError}
          </motion.p>
        )}
      </AnimatePresence>
    </form>
  );
}
